// The guided-flow engine's pure rules (see types.ts): branching, visibility per audience and
// access level, typed validation, step completeness, progress and the review list. The API runs
// these on every save and submit; the web runs the same functions to render, so the two never
// disagree about what is shown, what is required or what a value may be.
import type {
  Answers,
  AnswerValue,
  Audience,
  Condition,
  FieldDef,
  FlowDef,
  StepDef,
  Viewer,
} from "./types";

const DEFAULT_STEP_AUDIENCE: readonly Audience[] = ["manager", "assignee"];

/** Whether a condition holds over the answers (an absent answer is null). */
export function holds(c: Condition | undefined, answers: Answers): boolean {
  if (!c) return true;
  if ("all" in c) return c.all.every((x) => holds(x, answers));
  if ("any" in c) return c.any.some((x) => holds(x, answers));
  if ("not" in c) return !holds(c.not, answers);
  const v = answers[c.field] ?? null;
  if ("equals" in c) return v === c.equals;
  if ("in" in c) return (typeof v === "string" || typeof v === "number") && c.in.includes(v);
  return isFilledValue(v) === c.filled;
}

/** A value counts as filled when it says something: not null, not blank, not an empty list, not 0 rows. */
export function isFilledValue(v: AnswerValue | undefined): boolean {
  if (v === null || v === undefined || v === false) return false;
  if (typeof v === "string") return v.trim().length > 0;
  if (Array.isArray(v)) return v.length > 0;
  return true;
}

/** A boolean field is answered by `false` too: only null is "not answered". */
export function isAnswered(f: FieldDef, v: AnswerValue | undefined): boolean {
  if (f.type === "boolean") return v === true || v === false;
  // A linked editor's answer is its row count (0 = nothing yet); a confirm is `true` once confirmed.
  if (f.type === "linked") return typeof v === "number" ? v > 0 : isFilledValue(v);
  return isFilledValue(v);
}

const meets = (want: readonly Audience[], viewer: Viewer) =>
  want.some((a) => viewer.audiences.has(a));

/**
 * Whether the step applies at all, for anyone (its branch holds and someone may fill it): what
 * progress and the submit check count. A step hidden from a client-viewer assignee still applies
 * when a manager fills it.
 */
export function stepApplies(step: StepDef, answers: Answers, access: Viewer["assigneeAccess"]) {
  if (!holds(step.when, answers)) return false;
  return step.fields.some((f) => fieldApplies(step, f, answers, access));
}

/** Whether a field applies for someone (its branch holds, and it is not only the hidden assignee's). */
export function fieldApplies(
  step: StepDef,
  f: FieldDef,
  answers: Answers,
  access: Viewer["assigneeAccess"],
): boolean {
  if (!holds(f.when, answers)) return false;
  const assigneeHidden = access !== null && (step.hideForAccess ?? []).includes(access);
  const fillers = assigneeHidden ? f.fill.filter((a) => a !== "assignee") : f.fill;
  return fillers.length > 0;
}

/** Whether this viewer sees the step (its audience, its branch, the assignee's access level). */
export function stepVisible(step: StepDef, viewer: Viewer, answers: Answers): boolean {
  if (!holds(step.when, answers)) return false;
  const see = step.see ?? DEFAULT_STEP_AUDIENCE;
  // Seen through a role other than "assignee": the access level does not narrow it.
  if (see.some((a) => a !== "assignee" && viewer.audiences.has(a))) return true;
  if (!see.includes("assignee") || !viewer.audiences.has("assignee")) return false;
  return !(viewer.assigneeAccess && (step.hideForAccess ?? []).includes(viewer.assigneeAccess));
}

export function fieldVisible(f: FieldDef, viewer: Viewer, answers: Answers): boolean {
  if (!holds(f.when, answers)) return false;
  return f.see ? meets(f.see, viewer) : true;
}

export function fieldEditable(f: FieldDef, viewer: Viewer): boolean {
  return meets(f.fill, viewer);
}

/**
 * What the engine reads of a definition: its chapters. A registered flow (`FlowDef`) is one; so is
 * a definition filled outside a seat (ONBOARDING-P1's client intake, `ONBOARDING_INTAKE`).
 */
export type FlowChapters = Pick<FlowDef, "chapters">;

export function findStep(
  def: FlowChapters,
  stepKey: string,
): { chapter: string; step: StepDef } | null {
  for (const c of def.chapters)
    for (const s of c.steps) if (s.key === stepKey) return { chapter: c.key, step: s };
  return null;
}

export const allSteps = (def: FlowChapters): StepDef[] => def.chapters.flatMap((c) => [...c.steps]);

const TIME = /^([01]\d|2[0-3]):[0-5]\d$/;
const MONEY = /^\d{1,12}(\.\d{1,2})?$/;
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/;

const LINK = /^https?:\/\/[^\s/$.?#][^\s]*$/i;
/** COLLECT-FLOWS: how many items a list, links or files answer may hold, and each item's length. */
export const ITEM_LIMITS = {
  // A step's whole save is at most 16 KiB on the wire (FlowStepSave): keep one list well under.
  list: { items: 40, length: 300 },
  links: { items: 40, length: 300 },
  files: { items: 30, length: 36 },
} as const;

/** A list, links or files answer: trimmed items, blanks and repeats dropped, `[]` read as null. */
function checkItems(
  f: FieldDef,
  raw: unknown,
): { ok: true; value: AnswerValue } | { ok: false; message: string } {
  const fail = (message: string) => ({ ok: false as const, message });
  if (!Array.isArray(raw) || raw.some((x) => typeof x !== "string")) return fail("must be a list");
  const items = [...new Set((raw as string[]).map((x) => x.trim()).filter((x) => x !== ""))];
  const limit = ITEM_LIMITS[f.type as keyof typeof ITEM_LIMITS];
  if (items.length > limit.items) return fail(`at most ${limit.items} items`);
  for (const x of items) {
    if (x.length > limit.length) return fail(`each item at most ${limit.length} characters`);
    if (f.type === "links" && !LINK.test(x)) return fail(`"${x}" is not a web link (https://…)`);
    if (f.type === "files" && !UUID.test(x)) return fail("upload the file again");
  }
  return { ok: true, value: items.length ? items : null };
}

/** A typed value checked and normalised (trimmed text, null for blank), or the problem in words. */
export function checkValue(
  f: FieldDef,
  raw: unknown,
): { ok: true; value: AnswerValue } | { ok: false; message: string } {
  const fail = (message: string) => ({ ok: false as const, message });
  if (raw === null || raw === undefined) return { ok: true, value: null };
  switch (f.type) {
    case "linked":
      return fail("filled in its own editor");
    case "confirm":
      return raw === true ? { ok: true, value: true } : fail("send true to confirm");
    case "boolean":
      return typeof raw === "boolean" ? { ok: true, value: raw } : fail("must be true or false");
    case "number": {
      if (typeof raw !== "number" || !Number.isFinite(raw)) return fail("must be a number");
      if (f.min !== undefined && raw < f.min) return fail(`must be at least ${f.min}`);
      if (f.max !== undefined && raw > f.max) return fail(`must be at most ${f.max}`);
      return { ok: true, value: raw };
    }
    case "multiselect": {
      if (!Array.isArray(raw) || raw.some((x) => typeof x !== "string"))
        return fail("must be a list");
      const allowed = new Set((f.options ?? []).map((o) => o.value));
      const bad = raw.find((x) => !allowed.has(x as string));
      if (bad !== undefined) return fail(`"${String(bad)}" is not an option`);
      return { ok: true, value: [...new Set(raw as string[])] };
    }
    case "list":
    case "links":
    case "files":
      return checkItems(f, raw);
    default:
      break;
  }
  if (typeof raw !== "string") return fail("must be text");
  const text = raw.trim();
  if (text === "") return { ok: true, value: null };
  switch (f.type) {
    case "select":
      return (f.options ?? []).some((o) => o.value === text)
        ? { ok: true, value: text }
        : fail(`"${text}" is not an option`);
    case "time":
      return TIME.test(text) ? { ok: true, value: text } : fail("use HH:MM (24 h)");
    case "money":
      return MONEY.test(text) ? { ok: true, value: text } : fail("an amount like 1250.00");
    case "seat":
    case "role":
      return UUID.test(text) ? { ok: true, value: text } : fail("pick one from the list");
    default: {
      const max = f.maxLength ?? (f.type === "longtext" ? 8000 : 200);
      if (text.length > max) return fail(`at most ${max} characters`);
      if (f.pattern && !new RegExp(`^(?:${f.pattern})$`).test(text))
        return fail(f.patternHint ?? "not in the expected form");
      return { ok: true, value: text };
    }
  }
}

export type SaveCheck =
  | { ok: true; values: Record<string, AnswerValue> }
  | { ok: false; errors: { field: string; message: string }[] };

/**
 * What one caller may write on one step: every sent key is a field of the step, visible and
 * editable for them under the answers as they would be after the save, and of its type. Saving
 * is partial (save and resume): required fields are checked at submit, not here.
 */
export function checkSave(
  def: FlowChapters,
  stepKey: string,
  sent: Record<string, unknown>,
  viewer: Viewer,
  current: Answers,
): SaveCheck {
  const found = findStep(def, stepKey);
  if (!found) return { ok: false, errors: [{ field: "step", message: "no such step" }] };
  const { step } = found;
  const errors: { field: string; message: string }[] = [];
  const values: Record<string, AnswerValue> = {};
  for (const [key, raw] of Object.entries(sent)) {
    const f = step.fields.find((x) => x.key === key);
    if (!f) {
      errors.push({ field: key, message: "not a field of this step" });
      continue;
    }
    const checked = checkValue(f, raw);
    if (!checked.ok) errors.push({ field: key, message: checked.message });
    else values[key] = checked.value;
  }
  const after: Answers = { ...current, ...values };
  if (!stepVisible(step, viewer, after))
    return { ok: false, errors: [{ field: "step", message: "this step is not yours to fill" }] };
  for (const key of Object.keys(values)) {
    const f = step.fields.find((x) => x.key === key) as FieldDef;
    if (!fieldVisible(f, viewer, after) || !fieldEditable(f, viewer))
      errors.push({ field: key, message: "not yours to fill" });
  }
  return errors.length ? { ok: false, errors } : { ok: true, values };
}

export type Missing = { step: string; field: string; label: string; fill: readonly Audience[] };

/** Required under these answers (`optionalWhen` lifts it: the top of the chart reports to nobody). */
export const isRequired = (f: FieldDef, answers: Answers): boolean =>
  Boolean(f.required) && !(f.optionalWhen && holds(f.optionalWhen, answers));

/** The required fields that apply and are not answered, in flow order. */
export function missingFields(
  def: FlowChapters,
  answers: Answers,
  access: Viewer["assigneeAccess"],
): Missing[] {
  const out: Missing[] = [];
  for (const step of allSteps(def)) {
    if (!stepApplies(step, answers, access)) continue;
    for (const f of step.fields)
      if (
        isRequired(f, answers) &&
        fieldApplies(step, f, answers, access) &&
        !isAnswered(f, answers[f.key])
      )
        out.push({ step: step.key, field: f.key, label: f.label, fill: f.fill });
  }
  return out;
}

/** Types whose persisted answer is re-checked at submit (the rest are ids, counts or markers). */
const RECHECKED = new Set<FieldDef["type"]>([
  "text",
  "longtext",
  "number",
  "money",
  "select",
  "multiselect",
  "boolean",
  "time",
]);

/**
 * Answers that apply but no longer pass their field's check: the real fields are writable
 * outside the flow (seat settings, the profile), so submit re-checks what it finds there.
 */
export function invalidAnswers(
  def: FlowChapters,
  answers: Answers,
  access: Viewer["assigneeAccess"],
): (Missing & { message: string })[] {
  const out: (Missing & { message: string })[] = [];
  for (const step of allSteps(def)) {
    if (!stepApplies(step, answers, access)) continue;
    for (const f of step.fields) {
      const v = answers[f.key];
      // Only values free to change outside the flow: seat settings, the seat's own columns and
      // the DE's profile. A sealed field reads as a presence marker (checked when sealed); the
      // access level reads the membership's roles (an owner's "owner" is no invite option).
      const free = ["seat_setting", "seat", "de_profile"].includes(f.bind.kind);
      if (v === null || v === undefined || !RECHECKED.has(f.type) || !free) continue;
      if (!fieldApplies(step, f, answers, access)) continue;
      const checked = checkValue(f, v);
      if (!checked.ok)
        out.push({
          step: step.key,
          field: f.key,
          label: f.label,
          fill: f.fill,
          message: checked.message,
        });
    }
  }
  return out;
}

export type ChapterProgress = {
  key: string;
  title: string;
  complete: boolean;
  steps: { key: string; title: string; complete: boolean; saved: boolean }[];
};

export type Progress = {
  chapters: ChapterProgress[];
  chaptersDone: number;
  chaptersTotal: number;
  /** Whole percent of the applicable steps that are complete. */
  percent: number;
};

/**
 * Progress over the steps that apply: a step is complete when every required field that applies
 * is answered and it was saved at least once (an optional-only step counts once saved).
 */
export function progressOf(
  def: FlowChapters,
  answers: Answers,
  saved: readonly string[],
  access: Viewer["assigneeAccess"],
): Progress {
  const savedSet = new Set(saved);
  const missing = new Set(missingFields(def, answers, access).map((m) => m.step));
  let stepsTotal = 0;
  let stepsDone = 0;
  const chapters = def.chapters.flatMap((c): ChapterProgress[] => {
    const steps = c.steps
      .filter((s) => stepApplies(s, answers, access))
      .map((s) => {
        const hasRequired = s.fields.some((f) => isRequired(f, answers));
        const complete = !missing.has(s.key) && (hasRequired || savedSet.has(s.key));
        return { key: s.key, title: s.title, complete, saved: savedSet.has(s.key) };
      });
    if (steps.length === 0) return [];
    stepsTotal += steps.length;
    stepsDone += steps.filter((s) => s.complete).length;
    return [{ key: c.key, title: c.title, complete: steps.every((s) => s.complete), steps }];
  });
  return {
    chapters,
    chaptersDone: chapters.filter((c) => c.complete).length,
    chaptersTotal: chapters.length,
    percent: stepsTotal === 0 ? 100 : Math.round((stepsDone / stepsTotal) * 100),
  };
}

/** "Complete your seat setup, 3 of 5 chapters": the chat card and banner line. */
export function progressLine(def: FlowDef, p: Progress): string {
  return `${def.callToAction}, ${p.chaptersDone} of ${p.chaptersTotal} chapters`;
}

export type ReviewRow = {
  chapter: string;
  step: string;
  field: string;
  label: string;
  type: FieldDef["type"];
  value: AnswerValue;
  answered: boolean;
  required: boolean;
  editable: boolean;
};

/** The final review screen: every field this viewer sees, with its answer and whether it is theirs. */
export function reviewRows(def: FlowChapters, answers: Answers, viewer: Viewer): ReviewRow[] {
  const out: ReviewRow[] = [];
  for (const c of def.chapters)
    for (const s of c.steps) {
      if (!stepVisible(s, viewer, answers)) continue;
      for (const f of s.fields) {
        if (!fieldVisible(f, viewer, answers)) continue;
        const value = answers[f.key] ?? null;
        out.push({
          chapter: c.key,
          step: s.key,
          field: f.key,
          label: f.label,
          type: f.type,
          value,
          answered: isAnswered(f, value),
          required: isRequired(f, answers),
          editable: fieldEditable(f, viewer),
        });
      }
    }
  return out;
}
